import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    getFinancialJournalEntryIndex,
    getNonFinancialJournalEntryIndex,
} from "@common/helpers/transactions";

export interface JournalEntryLineForTransactionEdit {
    accountId: string;
    classIds?: string[];
    transactionId?: number | null;
}

export function areClassIdsEqual(
    classIdsA: string[] | undefined,
    classIdsB: string[] | undefined,
): boolean {
    const sortedClassIdsA = [...(classIdsA ?? [])].sort((a, b) =>
        a.localeCompare(b),
    );
    const sortedClassIdsB = [...(classIdsB ?? [])].sort((a, b) =>
        a.localeCompare(b),
    );

    if (sortedClassIdsA.length !== sortedClassIdsB.length) {
        return false;
    }

    return sortedClassIdsA.every(
        (classId, index) => classId === sortedClassIdsB[index],
    );
}

export function getPartRowIndices(
    lines: Array<{ transactionId?: number | null }>,
    transactionId: number,
): number[] {
    return lines.flatMap((line, index) =>
        line.transactionId === transactionId ? [index] : [],
    );
}

export function getNonFinancialJournalEntryRowIndex(
    lines: Array<{ transactionId?: number | null }>,
    transaction: Pick<TransactionListItem, "id" | "amount">,
): number | undefined {
    const partIndices = getPartRowIndices(lines, transaction.id);
    const localIndex = getNonFinancialJournalEntryIndex(transaction);
    if (partIndices.length === 0) {
        return localIndex;
    }
    return partIndices[localIndex];
}

export function getFinancialJournalEntryRowIndex(
    lines: Array<{ transactionId?: number | null }>,
    transaction: Pick<TransactionListItem, "id" | "amount">,
): number | undefined {
    const partIndices = getPartRowIndices(lines, transaction.id);
    const localIndex = getFinancialJournalEntryIndex(transaction);
    if (partIndices.length === 0) {
        return localIndex;
    }
    return partIndices[localIndex];
}

export function collectUniqueLineTransactionIds(
    lines: Array<{ transactionId?: number | null }>,
): number[] {
    const uniqueIds = new Set<number>();
    for (const line of lines) {
        if (line.transactionId != null) {
            uniqueIds.add(line.transactionId);
        }
    }
    return [...uniqueIds].sort((a, b) => a - b);
}

export function collectTransactionJournalEntryUpdates<
    T extends Pick<TransactionListItem, "id" | "amount">,
>(params: {
    lines: JournalEntryLineForTransactionEdit[];
    initialLines: JournalEntryLineForTransactionEdit[];
    memo: string;
    initialMemo: string;
    transactions: T[];
    ledgerBasis: AccountingLedgerBasis;
}): Array<{ transaction: T; update: UpdateTransactionDto }> {
    const updates: Array<{
        transaction: T;
        update: UpdateTransactionDto;
    }> = [];
    const hasMemoUpdate = params.memo !== params.initialMemo;

    for (const transaction of params.transactions) {
        const nonFinancialRow = getNonFinancialJournalEntryRowIndex(
            params.lines,
            transaction,
        );
        const initialNonFinancialRow = getNonFinancialJournalEntryRowIndex(
            params.initialLines,
            transaction,
        );
        if (
            nonFinancialRow === undefined ||
            initialNonFinancialRow === undefined
        ) {
            continue;
        }

        const line = params.lines[nonFinancialRow];
        const initialLine = params.initialLines[initialNonFinancialRow];
        if (!line || !initialLine) {
            continue;
        }

        const newAccountId = line.accountId;
        const classIds = line.classIds;
        const hasClassIdsUpdate = !areClassIdsEqual(
            classIds,
            initialLine.classIds,
        );
        const hasAccountForTransactionUpdate =
            !!newAccountId &&
            newAccountId.length > 0 &&
            newAccountId !== initialLine.accountId;

        if (
            !hasAccountForTransactionUpdate &&
            !hasClassIdsUpdate &&
            !hasMemoUpdate
        ) {
            continue;
        }

        const accountUpdate: UpdateTransactionDto =
            params.ledgerBasis === AccountingLedgerBasis.accruals
                ? { accrualAccountId: newAccountId }
                : { accountId: newAccountId };
        const update: UpdateTransactionDto = {
            ...(hasAccountForTransactionUpdate ? accountUpdate : {}),
            ...(hasClassIdsUpdate ? { classIds } : {}),
            ...(hasMemoUpdate ? { memo: params.memo } : {}),
        };

        updates.push({ transaction, update });
    }

    return updates;
}
